/**
 * Client-side device fingerprint — v4.3.91
 *
 * Stable per-browser identifier used by the TOTP "trust this device for 30
 * days" feature (api/auth/mfa/verify-login.js + lib/multiFactorAuth.js
 * trustDevice/isDeviceTrusted). The server never receives PII here — it
 * only ever sees the random ID, which is HMAC'd server-side with the MFA
 * encryption key before being stored, so even DB read access does not let
 * an attacker forge a "trusted device" cookie for another user.
 *
 * The fingerprint is intentionally NOT a hardware fingerprint:
 *   - clearing site data / using incognito → re-challenge on next login
 *     (this is the desired behaviour — privacy-first)
 *   - swapping browser → re-challenge
 *
 * Format: "v1." + 64 hex chars (33 chars total, well within the 16..256
 * length validation in api/initiate-login.js / api/auth/mfa/verify-login.js).
 */

const STORAGE_KEY = 'aureus_device_id_v1';

function randomHex(bytes: number): string {
  if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues) {
    const buf = new Uint8Array(bytes);
    window.crypto.getRandomValues(buf);
    return Array.from(buf).map((b) => b.toString(16).padStart(2, '0')).join('');
  }
  // Fallback (should never run in modern browsers): Math.random is NOT
  // cryptographically strong, but the fingerprint is HMAC'd server-side
  // and only used for "trust this device" — not for auth itself.
  let out = '';
  for (let i = 0; i < bytes; i++) {
    out += Math.floor(Math.random() * 256).toString(16).padStart(2, '0');
  }
  return out;
}

export function getDeviceFingerprint(): string {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return `v1.${randomHex(32)}`;
    }
    let id = window.localStorage.getItem(STORAGE_KEY);
    if (!id || !/^v1\.[0-9a-f]{64}$/.test(id)) {
      id = `v1.${randomHex(32)}`;
      window.localStorage.setItem(STORAGE_KEY, id);
    }
    return id;
  } catch {
    return `v1.${randomHex(32)}`;
  }
}
